/* eslint-disable import/no-unresolved */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Card } from '@heroui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
/* eslint-enable import/no-unresolved */
import {
  Question,
  QuestionType,
  type QuestionOption,
} from '../../../../utils/preScreening';
import { useFetchJobPosting } from '../../../../hooks/jobPosting/useFetchJobPosting';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { useAuth } from '../../../../hooks/useAuth';
import dashboardController from '../../../../services/api/dashboardController';
import { queryClient } from '../../../../lib/queryClient';
import { QUERY_KEYS } from '../../../../constants';
import Logger from '../../../../services/Logger';
import type { SettingsSaveHandler } from './types';

type AuthLike = { tokens?: { idToken?: { jwtToken?: string } } };

type Props = {
  jobId: string;
  parentSaveMode?: boolean;
  onSaveHandlerReady?: (handler: SettingsSaveHandler) => void;
};

type AnswerTypeId = 'radio' | 'short' | 'number';

type DisclosureTopic = {
  id: string;
  label: string;
  question: string;
  options: string[];
  legalText?: string;
};

const C = {
  bg: '#FAF9F7',
  surface: '#FFFFFF',
  raised: '#FDFCFB',
  border: '#E9E5DE',
  borderSoft: '#F1EEE8',
  text: '#1D1B18',
  text2: '#6E6960',
  text3: '#787367',
  text4: '#9D978E',
  terra: '#C04B2D',
  olive: '#557347',
  amber: '#8F6B1F',
  warmBg: '#F5F1EC',
  warmBgDeep: '#EDE8E1',
} as const;

const disclosureTopics: DisclosureTopic[] = [
  {
    id: 'age',
    label: 'Age',
    question: 'What is your age range?',
    options: [
      '18-24',
      '25-34',
      '35-44',
      '45-54',
      '55-64',
      '65+',
      'Prefer not to say',
    ],
    legalText:
      'Disclosure of age is optional and requested only for lawful reporting or compliance purposes. This information will remain confidential and will not influence hiring decisions.',
  },
  {
    id: 'pronouns',
    label: 'Pronouns',
    question: 'What are your pronouns?',
    options: ['He/Him', 'She/Her', 'They/Them', 'Other', 'Prefer not to say'],
    legalText:
      'Disclosure of pronouns is optional and requested solely to foster inclusive communication. This information will not be used for employment decision-making and will be kept confidential in accordance with applicable laws.',
  },
  {
    id: 'disabilityStatus',
    label: 'Disability status',
    question: 'Do you identify as a person with a disability?',
    options: [
      'Yes, I have a disability',
      'No, I do not have a disability',
      'Prefer not to say',
    ],
    legalText:
      'Disclosure of disability status is optional and is requested solely to fulfill requirements under applicable equal employment opportunity laws. The information you provide will be kept confidential, used only as permitted by law, and will not influence the hiring process.',
  },
  {
    id: 'workAuthorization',
    label: 'Work authorization',
    question: 'Are you authorized to work in this country?',
    options: ['Yes', 'No', 'Prefer not to say'],
    legalText:
      'Disclosure of work authorization is optional and requested only to comply with employment verification requirements. This information will be kept confidential and will not otherwise influence hiring decisions.',
  },
  {
    id: 'veteranStatus',
    label: 'Veteran status',
    question: 'What is your veteran status?',
    options: ['I am a veteran', 'I am not a veteran', 'Prefer not to say'],
    legalText:
      'Disclosure of veteran status is optional and requested to meet obligations under applicable equal employment opportunity laws. The information you provide will remain confidential, used only for authorized reporting, and will not influence the hiring process.',
  },
  {
    id: 'sponsorship',
    label: 'Sponsorship',
    question: 'Will you now or in the future require sponsorship?',
    options: ['Yes', 'No', 'Prefer not to say'],
    legalText:
      'Disclosure of sponsorship needs is requested only to comply with employment and immigration requirements. This information will be kept confidential, used solely for verification purposes, and will not negatively influence the hiring process.',
  },
  {
    id: 'gender',
    label: 'Gender',
    question: 'What is your gender?',
    options: ['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'],
    legalText:
      'Disclosure of gender is optional and is requested to support compliance with applicable equal employment opportunity regulations. The information you provide will be kept confidential and will not influence employment decisions.',
  },
  {
    id: 'friendsAndFamily',
    label: 'Friends & Family',
    question: 'Were you referred by a current employee?',
    options: ['Yes', 'No'],
    legalText:
      'Disclosure of personal relationships with current employees is optional and requested to maintain fairness and compliance in the hiring process. The information you provide will be kept confidential and will not adversely affect employment opportunities.',
  },
  {
    id: 'ethnicity',
    label: 'Ethnicity (EEO)',
    question: 'What is your ethnicity?',
    options: [
      'Hispanic or Latino',
      'White',
      'Black or African American',
      'Asian',
      'Native Hawaiian or Pacific Islander',
      'American Indian or Alaska Native',
      'Two or more races',
      'Prefer not to say',
    ],
    legalText:
      'Disclosure of race or ethnicity is optional and requested solely for equal employment opportunity reporting. This information will be kept confidential and will not influence hiring decisions.',
  },
];

const answerTypes: Array<{ id: AnswerTypeId; label: string }> = [
  { id: 'radio', label: 'Radio' },
  { id: 'short', label: 'Short answer' },
  { id: 'number', label: 'Number' },
];

const topicById = new Map(disclosureTopics.map((topic) => [topic.id, topic]));
const NEW_DROPDOWN_ID = '__new__';

const buildOptions = (texts: string[]): QuestionOption[] =>
  texts.map((text) => ({ id: crypto.randomUUID(), text }));

const defaultRadioOptions = (): QuestionOption[] =>
  buildOptions(['Yes', 'No', 'Prefer not to say']);

const getAnswerTypeId = (
  questionType: Question['questionType'],
): AnswerTypeId => {
  switch (questionType) {
    case QuestionType.Number:
      return 'number';
    case QuestionType.ShortAnswer:
      return 'short';
    case QuestionType.Radio:
    default:
      return 'radio';
  }
};

const getQuestionPatchForAnswerType = (
  question: Question,
  answerType: AnswerTypeId,
): Pick<Question, 'questionType' | 'expectedAns' | 'options'> => {
  const presetTopic = question.libraryId
    ? topicById.get(question.libraryId)
    : undefined;

  switch (answerType) {
    case 'radio': {
      let nextOptions = defaultRadioOptions();

      if (question.options && question.options.length > 0) {
        nextOptions = question.options;
      } else if (presetTopic) {
        nextOptions = buildOptions(presetTopic.options);
      }

      return {
        questionType: QuestionType.Radio,
        expectedAns: null,
        options: nextOptions,
      };
    }
    case 'number':
      return {
        questionType: QuestionType.Number,
        expectedAns: null,
        options: question.options ?? [],
      };
    case 'short':
    default:
      return {
        questionType: QuestionType.ShortAnswer,
        expectedAns: null,
        options: question.options ?? [],
      };
  }
};

const buildTopicQuestion = (topic: DisclosureTopic): Question => ({
  id: crypto.randomUUID(),
  libraryId: topic.id,
  question: topic.question,
  questionType: QuestionType.Radio,
  expectedAns: null,
  options: buildOptions(topic.options),
  legalText: topic.legalText ?? '',
});

const buildCustomQuestion = (
  questionText: string,
  answerType: AnswerTypeId,
): Question => {
  const trimmedQuestion = questionText.trim();

  switch (answerType) {
    case 'radio':
      return {
        id: crypto.randomUUID(),
        question: trimmedQuestion,
        questionType: QuestionType.Radio,
        expectedAns: null,
        options: defaultRadioOptions(),
        legalText: '',
      };
    case 'number':
      return {
        id: crypto.randomUUID(),
        question: trimmedQuestion,
        questionType: QuestionType.Number,
        expectedAns: null,
        options: [],
        legalText: '',
      };
    case 'short':
    default:
      return {
        id: crypto.randomUUID(),
        question: trimmedQuestion,
        questionType: QuestionType.ShortAnswer,
        expectedAns: null,
        options: [],
        legalText: '',
      };
  }
};

const QuestionBadge: React.FC<{ num: number }> = ({ num }) => (
  <div
    style={{
      width: 28,
      height: 28,
      borderRadius: 8,
      background: `${C.olive}08`,
      border: `1px solid ${C.olive}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 11, fontWeight: 660, color: C.olive }}>
      Q{num}
    </span>
  </div>
);

type AnswerTypeDropdownProps = {
  value: AnswerTypeId;
  onChange: (value: AnswerTypeId) => void;
  open: boolean;
  onToggle: (open: boolean) => void;
  fullWidth?: boolean;
};

const AnswerTypeDropdown: React.FC<AnswerTypeDropdownProps> = ({
  value,
  onChange,
  open,
  onToggle,
  fullWidth = false,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    const handler = (event: MouseEvent) => {
      if (
        ref.current &&
        event.target instanceof Node &&
        !ref.current.contains(event.target)
      ) {
        onToggle(false);
      }
    };

    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onToggle, open]);

  const selected = answerTypes.find((type) => type.id === value);

  return (
    <div
      ref={ref}
      style={{ position: 'relative', width: fullWidth ? '100%' : undefined }}
    >
      <button
        type="button"
        onClick={() => onToggle(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '7px 12px',
          borderRadius: 7,
          minWidth: fullWidth ? undefined : 130,
          width: fullWidth ? '100%' : undefined,
          border: `1px solid ${open ? `${C.olive}30` : C.border}`,
          background: C.surface,
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        <div style={{ minWidth: 0, textAlign: 'left' }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 560,
              color: C.text3,
              letterSpacing: '0.03em',
              marginBottom: 1,
            }}
          >
            Answer type
          </div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 540,
              color: C.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {selected?.label || 'Select'}
          </div>
        </div>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          style={{
            marginLeft: 10,
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s',
            flexShrink: 0,
          }}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke={C.text3}
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            left: fullWidth ? 0 : undefined,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(29,27,24,0.08)',
            padding: '4px',
            zIndex: 20,
            minWidth: fullWidth ? undefined : 160,
          }}
        >
          {answerTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => {
                onChange(type.id);
                onToggle(false);
              }}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                outline: 'none',
                borderRadius: 6,
                background: value === type.id ? `${C.olive}06` : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(event) => {
                if (value !== type.id) {
                  const { currentTarget } = event;
                  currentTarget.style.background = C.raised;
                }
              }}
              onMouseLeave={(event) => {
                if (value !== type.id) {
                  const { currentTarget } = event;
                  currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: value === type.id ? 560 : 440,
                  color: value === type.id ? C.olive : C.text,
                }}
              >
                {type.label}
              </span>
              {value === type.id && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 6L5.5 8.5L9.5 3.5"
                    stroke={C.olive}
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

AnswerTypeDropdown.defaultProps = {
  fullWidth: false,
};

const EmptyState: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <div
    style={{
      padding: '24px 28px',
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      background: C.surface,
    }}
  >
    <h2
      style={{
        fontSize: 15,
        fontWeight: 660,
        margin: '0 0 3px',
        color: C.text,
      }}
    >
      Voluntary disclosures
    </h2>
    <p
      style={{
        fontSize: 12.5,
        color: C.text3,
        margin: '0 0 24px',
        fontWeight: 420,
      }}
    >
      Set the questions which the candidate must answer in the voluntary
      disclosures.
    </p>

    <div
      style={{
        padding: '32px 28px',
        borderRadius: 10,
        border: `1px dashed ${C.border}`,
        background: C.raised,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: `${C.olive}06`,
          border: `1px solid ${C.olive}12`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
          <rect
            x="4"
            y="1.5"
            width="8"
            height="3"
            rx="1"
            stroke={C.olive}
            strokeWidth="1.1"
          />
          <rect
            x="2.5"
            y="3.5"
            width="11"
            height="11"
            rx="1.5"
            stroke={C.olive}
            strokeWidth="1.1"
          />
          <circle cx="8" cy="9.5" r="1.5" stroke={C.olive} strokeWidth="1.0" />
          <path
            d="M8 8V7"
            stroke={C.olive}
            strokeWidth="1.0"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: C.text,
          marginBottom: 4,
          textAlign: 'center',
        }}
      >
        Add voluntary disclosure questions
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: C.text3,
          fontWeight: 420,
          textAlign: 'center',
          maxWidth: 360,
          lineHeight: 1.55,
          marginBottom: 18,
        }}
      >
        Add necessary{' '}
        <span style={{ fontWeight: 580, color: C.text2 }}>
          personal questions
        </span>{' '}
        candidates must answer to be considered for this role.
      </div>

      <div
        style={{
          fontSize: 11,
          fontWeight: 540,
          color: C.text3,
          marginBottom: 10,
        }}
      >
        Examples
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          justifyContent: 'center',
          marginBottom: 20,
        }}
      >
        {[
          'What is your gender?',
          'What is your Veteran Status?',
          'Do you identify yourself with disability?',
        ].map((example) => (
          <span
            key={example}
            style={{
              fontSize: 12,
              fontWeight: 440,
              color: C.text2,
              padding: '5px 12px',
              borderRadius: 6,
              background: C.warmBg,
              border: `1px solid ${C.borderSoft}`,
            }}
          >
            {example}
          </span>
        ))}
      </div>

      <button
        type="button"
        onClick={onAdd}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '9px 20px',
          borderRadius: 8,
          border: 'none',
          background: C.text,
          color: '#fff',
          fontSize: 13,
          fontWeight: 560,
          cursor: 'pointer',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 2.5V9.5M2.5 6H9.5"
            stroke="#fff"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
        Add question
      </button>
    </div>
  </div>
);

const DisclosureSettings: React.FC<Props> = ({
  jobId,
  parentSaveMode = false,
  onSaveHandlerReady,
}) => {
  const { organization } = useOrganization();
  const orgId = organization?.orgUuid || '';
  const auth = useAuth();
  const authToken = (auth as AuthLike)?.tokens?.idToken?.jwtToken ?? '';
  const { data: jobData, isLoading } = useFetchJobPosting(jobId);
  const fetchedQuestionsKey = useMemo(
    () =>
      JSON.stringify(
        ((jobData as { disclosureQuestions?: Question[] })
          ?.disclosureQuestions ?? []) as Question[],
      ),
    [jobData],
  );
  const lastHydratedQuestionsKeyRef = useRef<string>('');
  const newQuestionInputRef = useRef<HTMLInputElement | null>(null);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [savedQuestions, setSavedQuestions] = useState<Question[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [editingNew, setEditingNew] = useState(false);
  const [newText, setNewText] = useState('');
  const [newType, setNewType] = useState<AnswerTypeId>('radio');

  useEffect(() => {
    if (fetchedQuestionsKey === lastHydratedQuestionsKeyRef.current) return;

    lastHydratedQuestionsKeyRef.current = fetchedQuestionsKey;
    const existingQuestions = JSON.parse(fetchedQuestionsKey) as Question[];

    setQuestions(existingQuestions);
    setSavedQuestions(existingQuestions);
    setSaveError(null);
    setEditingNew(false);
    setNewText('');
    setNewType('radio');
    setOpenDropdown(null);
  }, [fetchedQuestionsKey]);

  useEffect(() => {
    if (!editingNew) return;
    newQuestionInputRef.current?.focus();
  }, [editingNew]);

  const hasUnsavedChanges =
    JSON.stringify(questions) !== JSON.stringify(savedQuestions);

  const activeTopicIds = useMemo(
    () =>
      new Set(
        questions
          .map((question) => question.libraryId)
          .filter(Boolean) as string[],
      ),
    [questions],
  );

  const updateQuestion = useCallback((id: string, patch: Partial<Question>) => {
    setQuestions((prev) =>
      prev.map((question) =>
        question.id === id ? { ...question, ...patch } : question,
      ),
    );
  }, []);

  const handleOptionChange = useCallback(
    (qid: string, oid: string, text: string) => {
      setQuestions((prev) =>
        prev.map((question) =>
          question.id === qid
            ? {
                ...question,
                options: (question.options ?? []).map((option) =>
                  option.id === oid ? { ...option, text } : option,
                ),
              }
            : question,
        ),
      );
    },
    [],
  );

  const handleAddOption = useCallback((qid: string) => {
    setQuestions((prev) =>
      prev.map((question) =>
        question.id === qid
          ? {
              ...question,
              options: [
                ...(question.options ?? []),
                { id: crypto.randomUUID(), text: '' },
              ],
            }
          : question,
      ),
    );
  }, []);

  const handleRemoveOption = useCallback((qid: string, oid: string) => {
    setQuestions((prev) =>
      prev.map((question) =>
        question.id === qid
          ? {
              ...question,
              options: (question.options ?? []).filter(
                (option) => option.id !== oid,
              ),
            }
          : question,
      ),
    );
  }, []);

  const handleToggleTopic = useCallback((topic: DisclosureTopic) => {
    setQuestions((prev) => {
      const isActive = prev.some((question) => question.libraryId === topic.id);
      if (isActive) {
        return prev.filter((question) => question.libraryId !== topic.id);
      }
      return [...prev, buildTopicQuestion(topic)];
    });
  }, []);

  const handleRemoveQuestion = useCallback((id: string) => {
    setQuestions((prev) => prev.filter((question) => question.id !== id));
    setOpenDropdown((current) => (current === id ? null : current));
  }, []);

  const handleQuestionTypeChange = useCallback(
    (id: string, answerType: AnswerTypeId) => {
      setQuestions((prev) =>
        prev.map((question) =>
          question.id === id
            ? {
                ...question,
                ...getQuestionPatchForAnswerType(question, answerType),
              }
            : question,
        ),
      );
    },
    [],
  );

  const handleAddCustom = useCallback(() => {
    if (!newText.trim()) return;

    setQuestions((prev) => [...prev, buildCustomQuestion(newText, newType)]);
    setNewText('');
    setNewType('radio');
    setEditingNew(false);
    setOpenDropdown((current) =>
      current === NEW_DROPDOWN_ID ? null : current,
    );
  }, [newText, newType]);

  const handleSave = useCallback(async () => {
    if (!orgId || !authToken || !jobId) return;

    setSaving(true);
    setSaveError(null);

    try {
      await dashboardController.updateJobPosting(
        {
          orgId,
          jobId,
          disclosureQuestions: questions,
        },
        authToken,
      );

      setSavedQuestions(questions);

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_POSTING(orgId, jobId),
      });
    } catch (error) {
      setSaveError('Failed to save disclosure questions. Please try again.');
      Logger.LogError(error, {
        context: 'save_disclosure_questions',
        orgId,
        jobId,
      });
    } finally {
      setSaving(false);
    }
  }, [authToken, jobId, orgId, questions]);

  const handleReset = useCallback(() => {
    setQuestions(savedQuestions);
    setSaveError(null);
    setEditingNew(false);
    setNewText('');
    setNewType('radio');
    setOpenDropdown(null);
  }, [savedQuestions]);

  useEffect(() => {
    if (!onSaveHandlerReady) return;

    onSaveHandlerReady({
      hasUnsavedChanges,
      save: handleSave,
      reset: handleReset,
    });
  }, [handleReset, handleSave, hasUnsavedChanges, onSaveHandlerReady]);

  if (isLoading) {
    return (
      <Card
        className="rounded-[12px] border shadow-none"
        style={{ borderColor: C.border, background: C.surface }}
      >
        <div className="px-6 py-10 text-center text-[#787367]">
          Loading disclosure settings...
        </div>
      </Card>
    );
  }

  const showEmptyState = questions.length === 0 && !editingNew;

  return (
    <section className="w-full">
      <Card
        className="!overflow-visible rounded-[12px] border shadow-none"
        style={{ borderColor: C.border, background: C.surface }}
      >
        <div className="px-5 py-5 sm:px-7 sm:py-6">
          {saveError && (
            <div
              className="mb-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm"
              style={{ background: '#FEF2F2', color: '#B91C1C' }}
            >
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" />
              {saveError}
            </div>
          )}

          {showEmptyState ? (
            <EmptyState onAdd={() => setEditingNew(true)} />
          ) : (
            <>
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 660,
                  margin: '0 0 3px',
                  color: C.text,
                }}
              >
                Voluntary disclosures
              </h2>
              <p
                style={{
                  fontSize: 12.5,
                  color: C.text3,
                  margin: '0 0 18px',
                  fontWeight: 420,
                }}
              >
                Set the questions which the candidate must answer in the
                voluntary disclosures.
              </p>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginBottom: 20,
                }}
              >
                {disclosureTopics.map((topic) => {
                  const isActive = activeTopicIds.has(topic.id);
                  return (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => handleToggleTopic(topic)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 7,
                        border: `1px solid ${isActive ? `${C.olive}30` : C.borderSoft}`,
                        background: isActive ? `${C.olive}06` : C.surface,
                        fontSize: 12,
                        fontWeight: isActive ? 520 : 460,
                        color: isActive ? C.olive : C.text2,
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(event) => {
                        if (!isActive) {
                          const { currentTarget } = event;
                          currentTarget.style.borderColor = C.border;
                          currentTarget.style.background = C.raised;
                        }
                      }}
                      onMouseLeave={(event) => {
                        if (!isActive) {
                          const { currentTarget } = event;
                          currentTarget.style.borderColor = C.borderSoft;
                          currentTarget.style.background = C.surface;
                        }
                      }}
                    >
                      {topic.label}
                      {isActive ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M3 6L5.5 8.5L9.5 3.5"
                            stroke={C.olive}
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M5 2.5V7.5M2.5 5H7.5"
                            stroke={C.text4}
                            strokeWidth="1.2"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-3">
                {questions.map((question, index) => {
                  const answerTypeId = getAnswerTypeId(question.questionType);
                  return (
                    <div
                      key={question.id}
                      style={{
                        padding: '16px 20px',
                        borderRadius: 10,
                        border: `1px solid ${C.borderSoft}`,
                        background: C.raised,
                      }}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 flex-1 items-start gap-[10px]">
                          <QuestionBadge num={index + 1} />
                          <input
                            value={question.question}
                            onChange={(event) =>
                              updateQuestion(question.id, {
                                question: event.target.value,
                              })
                            }
                            placeholder="Type your question..."
                            style={{
                              flex: 1,
                              minWidth: 0,
                              padding: '1px 0',
                              border: 'none',
                              outline: 'none',
                              fontSize: 14,
                              fontWeight: 540,
                              color: C.text,
                              background: 'transparent',
                              fontFamily: 'inherit',
                              lineHeight: 1.45,
                            }}
                          />
                        </div>

                        <div className="flex w-full items-start gap-2 sm:w-auto sm:flex-none">
                          <div className="min-w-0 flex-1 sm:w-auto sm:flex-none">
                            <AnswerTypeDropdown
                              value={answerTypeId}
                              onChange={(value) =>
                                handleQuestionTypeChange(question.id, value)
                              }
                              open={openDropdown === question.id}
                              onToggle={(value) =>
                                setOpenDropdown(value ? question.id : null)
                              }
                              fullWidth
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveQuestion(question.id)}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              border: `1px solid ${C.borderSoft}`,
                              background: C.surface,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              outline: 'none',
                              flexShrink: 0,
                            }}
                            onMouseEnter={(event) => {
                              const { currentTarget } = event;
                              currentTarget.style.borderColor = `${C.terra}30`;
                            }}
                            onMouseLeave={(event) => {
                              const { currentTarget } = event;
                              currentTarget.style.borderColor = C.borderSoft;
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M3 3L9 9M9 3L3 9"
                                stroke={C.text4}
                                strokeWidth="1.2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {answerTypeId === 'radio' && (
                        <div className="pl-0 pt-4 sm:pl-[38px] sm:pt-0">
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: 4,
                              gap: 12,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 540,
                                color: C.text3,
                              }}
                            >
                              Options
                            </span>
                          </div>
                          <div className="space-y-2">
                            {(question.options ?? []).map(
                              (option, optionIndex) => (
                                <div
                                  key={option.id}
                                  className="flex items-center gap-2"
                                >
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 520,
                                      color: C.text3,
                                      width: 14,
                                      flexShrink: 0,
                                      textAlign: 'right',
                                    }}
                                  >
                                    {optionIndex + 1}
                                  </span>
                                  <input
                                    aria-label={`Option ${optionIndex + 1}`}
                                    value={option.text}
                                    onChange={(event) =>
                                      handleOptionChange(
                                        question.id,
                                        option.id,
                                        event.target.value,
                                      )
                                    }
                                    placeholder="Please enter an option"
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      border: 'none',
                                      outline: 'none',
                                      background: 'transparent',
                                      fontSize: 13,
                                      fontWeight: 460,
                                      color: C.text,
                                      fontFamily: 'inherit',
                                      padding: '6px 0',
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="flex-none text-[#6B7280] hover:text-[#EF4444] text-sm"
                                    onClick={() =>
                                      handleRemoveOption(question.id, option.id)
                                    }
                                    aria-label={`Delete option ${optionIndex + 1}`}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ),
                            )}
                          </div>
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => handleAddOption(question.id)}
                              className="flex items-center gap-2 rounded-md border border-dashed border-[#D1D5DB] bg-white px-3 py-2 text-xs font-medium text-[#6B7280] transition-colors hover:border-[#8510DF] hover:text-[#8510DF]"
                            >
                              <span className="text-base leading-none">+</span>
                              Add Option
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {editingNew ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: '16px 20px',
                    borderRadius: 10,
                    border: `1px solid ${C.olive}20`,
                    background: C.raised,
                  }}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 flex-1 items-start gap-[10px]">
                      <QuestionBadge num={questions.length + 1} />
                      <input
                        ref={newQuestionInputRef}
                        value={newText}
                        onChange={(event) => setNewText(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') handleAddCustom();
                          if (event.key === 'Escape') {
                            setEditingNew(false);
                            setNewText('');
                            setNewType('radio');
                            setOpenDropdown((current) =>
                              current === NEW_DROPDOWN_ID ? null : current,
                            );
                          }
                        }}
                        placeholder="Type your question..."
                        style={{
                          flex: 1,
                          minWidth: 0,
                          padding: '8px 0',
                          border: 'none',
                          outline: 'none',
                          fontSize: 14,
                          fontWeight: 460,
                          color: C.text,
                          background: 'transparent',
                          fontFamily: 'inherit',
                        }}
                      />
                    </div>
                    <div className="w-full sm:w-auto sm:flex-none">
                      <AnswerTypeDropdown
                        value={newType}
                        onChange={setNewType}
                        open={openDropdown === NEW_DROPDOWN_ID}
                        onToggle={(value) =>
                          setOpenDropdown(value ? NEW_DROPDOWN_ID : null)
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 pt-[14px] sm:flex-row sm:gap-2 sm:pl-[38px]">
                    <button
                      type="button"
                      onClick={handleAddCustom}
                      style={{
                        padding: '7px 16px',
                        borderRadius: 7,
                        border: 'none',
                        background: C.text,
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 540,
                        cursor: 'pointer',
                      }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingNew(false);
                        setNewText('');
                        setNewType('radio');
                        setOpenDropdown((current) =>
                          current === NEW_DROPDOWN_ID ? null : current,
                        );
                      }}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 7,
                        border: `1px solid ${C.border}`,
                        background: C.surface,
                        color: C.text3,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingNew(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%',
                    marginTop: 12,
                    padding: '12px',
                    borderRadius: 10,
                    border: `1px dashed ${C.border}`,
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: C.text3,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(event) => {
                    const { currentTarget } = event;
                    currentTarget.style.borderColor = `${C.olive}40`;
                    currentTarget.style.color = C.olive;
                  }}
                  onMouseLeave={(event) => {
                    const { currentTarget } = event;
                    currentTarget.style.borderColor = C.border;
                    currentTarget.style.color = C.text3;
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M6 2.5V9.5M2.5 6H9.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add question
                </button>
              )}
            </>
          )}
        </div>

        {!parentSaveMode && hasUnsavedChanges && (
          <div
            className="sticky bottom-0 mt-5 rounded-b-[12px] border-t px-5 py-[14px] sm:px-7"
            style={{
              background: C.warmBg,
              borderColor: C.border,
            }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <span style={{ fontSize: 13, fontWeight: 580, color: C.text }}>
                  Unsaved changes
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: C.text3,
                    marginLeft: 8,
                  }}
                >
                  Changes across voluntary disclosures
                </span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-2">
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    padding: '8px 16px',
                    borderRadius: 7,
                    border: `1px solid ${C.border}`,
                    background: C.surface,
                    color: C.text2,
                    fontSize: 12.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6C2 3.8 3.8 2 6 2M10 6C10 8.2 8.2 10 6 10"
                      stroke={C.text3}
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M6 2L4 4M6 2L8 4"
                      stroke={C.text3}
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Reset all
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    padding: '8px 18px',
                    borderRadius: 7,
                    border: 'none',
                    background: C.olive,
                    color: '#fff',
                    fontSize: 12.5,
                    fontWeight: 560,
                    cursor: saving ? 'default' : 'pointer',
                    opacity: saving ? 0.75 : 1,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M3 6L5.5 8.5L9.5 3.5"
                      stroke="#fff"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {saving ? 'Saving...' : 'Save all changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
};

DisclosureSettings.defaultProps = {
  parentSaveMode: false,
  onSaveHandlerReady: undefined,
};

export default DisclosureSettings;
